import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DialogTitle,
  DialogContent,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Typography,
  Box,
  Link,
  LinearProgress,
} from '@mui/material';
import Theme from 'theme';
import { useMutation } from 'react-query';
import { ToastProps, ToastType } from 'components/Common/Toast';
import { createFreeTrialProvider, sendFreeTrialOtp } from 'pages/Dashboard/services/freeTrial.service';
import http from 'utils/http';
import { trackMixpanelEvent, logSentryError } from 'utils/utilMethods';
import maskEmail from 'pages/FreeTrialActivation/components/emailMaskUtil';
import { MixpanelEventName } from 'utils/constants';
import { useFreeTrialOnboarding } from 'pages/FreeTrialActivation/components/FreeTrialOnboardingContext';
import { auth } from 'utils/spaUrls';

interface CreateFreeAccountProps {
  onNext?: () => void;
  setEmailMasked?: (maskedEmail: string) => void;
  setProviderId?: (providerId: string) => void;
  setToastProps: (props: ToastProps | null) => void;
  step?: number;
  totalSteps?: number;
}

const TEMP_EMAIL_DOMAINS = (process.env.REACT_APP_TEMP_EMAIL_DOMAINS || '').split(',').map((d) => d.trim().toLowerCase());

function isTempEmail(email: string) {
  const domain = email.split('@')[1]?.toLowerCase() || '';
  return TEMP_EMAIL_DOMAINS.some((d) => domain.includes(d));
}

export default function CreateFreeAccount({
  onNext,
  setEmailMasked,
  setProviderId,
  setToastProps,
  step,
  totalSteps = 6,
}: CreateFreeAccountProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const trialDays = searchParams.get('freetrialdays') || '30';
  const trialCode = searchParams.get('freetrialcode') || '';
  const referralCode = searchParams.get('freetrialreferralcode') || '';
  const eventProps = useMemo(() => ({
    trial_days: trialDays,
    trial_code: trialCode,
    referral_code: referralCode,
  }), [trialDays, trialCode, referralCode]);
  React.useEffect(() => {
    trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_CREATE_ACCOUNT_DIALOG_VIEWED, eventProps);
  }, [eventProps]);

  const { fields, setFields, setStep, setOnboardingStatus } = useFreeTrialOnboarding();
  const [showWarning, setShowWarning] = useState(false);
  const [emailError, setEmailError] = useState('');

  const sendOtpMutation = useMutation({
    mutationFn: (otpProviderId: string) => sendFreeTrialOtp(http.post, otpProviderId),
    onError: (error) => {
      trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_CREATE_ACCOUNT_SEND_OTP_ERROR, eventProps);
      logSentryError(`Error sending OTP - ${(error as Error)?.message}`);
      setToastProps({
        message: 'Failed to send verification code. Please try again.',
        open: true,
        type: ToastType.error,
      });
    },
    onSuccess: () => {
      trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_CREATE_ACCOUNT_SEND_OTP_SUCCESS, eventProps);
      if (onNext) onNext();
    },
  });

  const createMutation = useMutation({
    mutationFn: (payload: {
      firstName: string;
      lastName: string; email: string
    }) => createFreeTrialProvider(http.post, {
      request: {
        name: {
          firstName: payload.firstName.trim(),
          lastName: payload.lastName.trim(),
        },
        email: payload.email.trim(),
      },
      referralCode: referralCode.trim(),
    }),
    onError: (error) => {
      trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_CREATE_ACCOUNT_ERROR, eventProps);
      logSentryError(`Error creating account - ${(error as Error)?.message}`);
      setToastProps({
        message: 'Failed to create account. Please try again.',
        open: true,
        type: ToastType.error,
      });
    },
    onSuccess: (data) => {
      if (data?.onboardingStatus) setOnboardingStatus(data.onboardingStatus);
      trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_CREATE_ACCOUNT_SUCCESS, eventProps);
      // Mask email and set
      if (setEmailMasked) {
        setEmailMasked(maskEmail(fields.email.trim()));
      }
      // Expose providerId to parent
      if (data?.providerId && setProviderId) {
        setProviderId(data.providerId);
      }

      if (data?.onboardingStatus === 'EXISTING_PROVIDER') {
        logSentryError('Error creating account - Existing provider tried to create account');
        navigate(auth.login);
      }
      // If onboardingStatus is PRACTICE_ASSOCIATED, call sendOtpMutation and go to VerifyEmail step
      if (data?.onboardingStatus === 'PRACTICE_ASSOCIATED' && data?.providerId) {
        sendOtpMutation.mutate(data.providerId);
        setStep(2);
      }
      // If onboardingStatus is PASSWORD_SET, only go to VerifyEmail
      else if (data?.onboardingStatus === 'PASSWORD_SET' && data?.providerId) {
        sendOtpMutation.mutate(data.providerId);
        setStep(2);
      }
      else if (data?.onboardingStatus === 'ONBOARDING_COMPLETED' && data?.providerId) {
        sendOtpMutation.mutate(data.providerId);
        setStep(2);
      }
      else if ((data?.onboardingStatus === '' || data?.onboardingStatus === 'ONBOARDING_STARTED') && onNext) {
        onNext();
      }
      else if (!data?.providerId || !setProviderId) {
        setToastProps({
          message: 'Account created but could not send verification code.',
          open: true,
          type: ToastType.error,
        });
      }
    },
  });

  const handleFieldChange = (field: keyof typeof fields) => (e: React.
    ChangeEvent<HTMLInputElement>) => {
    const value = field === 'checked' ? e.target.checked : e.target.value;
    setFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowWarning(false);
    setEmailError('');
    trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_CREATE_JOIN_FOR_FREE_BUTTON_CLICKED, {
      firstName: fields.firstName.trim(),
      lastName: fields.lastName.trim(),
      email: fields.email.trim(),
      ...eventProps,
    });
    if (!fields.firstName || !fields.lastName || !fields.email || !fields.checked) {
      setShowWarning(true);
      return;
    }

    if (isTempEmail(fields.email)) {
      setEmailError('Temporary email addresses aren’t supported. Enter a different email to sign up.');
      return;
    }
    createMutation.mutate({
      firstName: fields.firstName.trim(),
      lastName: fields.lastName.trim(),
      email: fields.email.trim(),
    });
  };

  return (
    <>
      <DialogTitle sx={{ fontWeight: 400, fontSize: 34, pb: 0 }}>
        Create your free account
      </DialogTitle>
      <LinearProgress
        variant='determinate'
        value={(step as number / totalSteps as number) * 100}
        color='success'
        sx={{
          width: '100%',
          borderRadius: '24px 24px 0 0',
          height: 4,
          my: 1,
        }}
      />
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant='body1' mb={3} color={Theme.custom.colors.textSecondary}>
          {trialDays}
          -day free trial. Cancel anytime.
        </Typography>
        <Box component='form' autoComplete='off' onSubmit={handleSubmit}>
          <TextField
            label='First name'
            name='firstName'
            variant='outlined'
            sx={{ mb: 2, width: '60%' }}
            value={fields.firstName}
            onChange={handleFieldChange('firstName')}
            required
          />
          <TextField
            label='Last name'
            name='lastName'
            variant='outlined'
            sx={{ mb: 2, width: '60%' }}
            value={fields.lastName}
            onChange={handleFieldChange('lastName')}
            required
          />
          <TextField
            label='Email address'
            name='email'
            variant='outlined'
            fullWidth
            sx={{ mb: 2, width: '80%' }}
            type='email'
            value={fields.email}
            onChange={handleFieldChange('email')}
            required
            className='fs-mask'
            error={!!emailError}
            helperText={emailError}
          />
          <FormControlLabel
            control={(
              <Checkbox
                name='checked'
                color='primary'
                sx={{ p: 0.5, mr: 1, ml: 1, alignSelf: 'flex-start' }}
                checked={fields.checked}
                onChange={handleFieldChange('checked')}
                required
              />
            )}
            label={(
              <Typography variant='body1' sx={{ lineHeight: 1.5 }}>
                I have read and agree to the
                {' '}
                <Link
                  href='https://www.headlamp.com/terms'
                  target='_blank'
                  underline='hover'
                  color={Theme.custom.colors.lightPrimaryMain}
                  fontSize={16}
                  fontWeight={400}
                >
                  Terms of Service
                </Link>
                ,
                {' '}
                <Link
                  href='https://www.headlamp.com/baa'
                  target='_blank'
                  underline='hover'
                  color={Theme.custom.colors.lightPrimaryMain}
                  fontSize={16}
                  fontWeight={400}
                >
                  Business Associate Agreement
                </Link>
                , and
                {' '}
                <Link
                  href='https://www.headlamp.com/privacy'
                  target='_blank'
                  underline='hover'
                  color={Theme.custom.colors.lightPrimaryMain}
                  fontSize={16}
                  fontWeight={400}
                >
                  Privacy Policy
                </Link>
              </Typography>
            )}
            sx={{
              alignItems: 'center',
              mb: 2,
              mt: 1,
              width: '100%',
              '.MuiFormControlLabel-label': { flex: 1 },
            }}
          />
          {showWarning && (
            <Typography color='error' mb={1} variant='body2'>
              Please fill in all required fields and agree to the terms.
            </Typography>
          )}
          <Button
            variant='contained'
            sx={{
              borderRadius: 8,
              boxShadow: 'none',
              fontSize: 15,
              py: 1.5,
              px: 6,
              mt: 1,
              background: Theme.custom.colors.lightPrimaryMain,
              color: Theme.custom.colors.white,
            }}
            type='submit'
            disabled={createMutation.isLoading}
          >
            JOIN FOR FREE
          </Button>
        </Box>
      </DialogContent>
    </>
  );
}
